import { CLASS_GROUP_LABELS, CLASS_GROUPS } from "#constants/classOptions"
import {
  CLASS_GROUP_TO_SKILL_TYPE,
  type CoreSkillLevel,
  type DigitalSkillLevel,
  type SculptureSkillLevel,
} from "#constants/skillLevelConstants"
import { type ClassConstructor } from "#customTypes/composedTypes/classTypes"
import {
  type StudentSkillHistory,
  StudentSkillType,
} from "#customTypes/composedTypes/studentTypes"

import { getCurrentSemester } from "./semesterHelpers"
import { addNewSkillEntry } from "./studentHelpers"
import {
  getSkillLevelAsOf,
  type StudentSkillSource,
} from "./studentSkillHelpers"

/**
 * Enrollment skill-level requirement (JARVIS-43).
 *
 * A student must have a skill level in the class group's skill type before
 * they can be enrolled in a NON-CAMP class. Camps are exempt because students
 * do not try out for them. Student creation never requires a level — the
 * tryout sets it — so this rule is enforced at every *enrollment* entry point:
 *
 *   - AddStudentToClassForm.handleSubmit (class roster "add / edit student")
 *   - TryoutProgressFormModal.handleFinish (Step 3 enrollment) — wrapped by
 *     tryoutHelpers.getTryoutEnrollmentBlock, which refuses a FAILED tryout
 *     first and tailors the missing-level copy to the tryout result
 *   - SwitchClassModal.handleSubmit (moving a student between classes)
 *
 * AddMakeupFormModal is deliberately NOT guarded: it adds the student to a
 * session's makeup roster, not to a class enrollment.
 *
 * Ladder mapping (via CLASS_GROUP_TO_SKILL_TYPE): CORE, PAINTING and
 * PORTFOLIO all validate against the core ladder; DIGITAL_ART against
 * digital; SCULPTURE against sculpture. The level itself is resolved from
 * `skillHistory` for the CLASS's semester by `getSkillLevelAsOf`.
 */

export type AnySkillLevel =
  | CoreSkillLevel
  | DigitalSkillLevel
  | SculptureSkillLevel

export interface EnrollmentSkillLevelRequirement {
  classGroup: CLASS_GROUPS
  /** Ladder the class group is measured on. */
  skillType: StudentSkillType
  /** Human label for messages, e.g. "Core" or "Digital Art". */
  groupLabel: string
  /** Semester the level has to cover — the class's own. */
  semester: string
}

type ClassRequirementFields = Pick<
  ClassConstructor,
  "group" | "isSummerCamp" | "semester"
>

/**
 * The skill-level requirement for enrolling in `classObj`, or `null` when
 * none applies (camps, or no class yet). Unknown/missing class groups fall
 * back to CORE, matching TryoutProgressFormModal's default.
 */
export const getEnrollmentSkillLevelRequirement = (
  classObj: ClassRequirementFields | null | undefined
): EnrollmentSkillLevelRequirement | null => {
  if (!classObj) return null
  if (classObj.isSummerCamp === true) return null

  const classGroup = classObj.group ?? CLASS_GROUPS.CORE
  const skillType =
    CLASS_GROUP_TO_SKILL_TYPE[classGroup] ?? StudentSkillType.core

  return {
    classGroup,
    skillType,
    groupLabel: CLASS_GROUP_LABELS[classGroup] ?? classGroup,
    semester: classObj.semester || getCurrentSemester(),
  }
}

/** True when the student has a level covering the class's semester. */
export const hasRequiredSkillLevel = (
  student: StudentSkillSource | null | undefined,
  requirement: EnrollmentSkillLevelRequirement | null
): boolean => {
  if (!requirement) return true

  return !!getSkillLevelAsOf(
    student,
    requirement.skillType,
    requirement.semester
  )
}

/**
 * The requirement the student does NOT yet satisfy for this class, or `null`
 * when they may be enrolled as-is. This is the single check every enrollment
 * path should call.
 */
export const getMissingSkillLevelForEnrollment = (
  student: StudentSkillSource | null | undefined,
  classObj: ClassRequirementFields | null | undefined
): EnrollmentSkillLevelRequirement | null => {
  const requirement = getEnrollmentSkillLevelRequirement(classObj)
  if (!requirement) return null
  return hasRequiredSkillLevel(student, requirement) ? null : requirement
}

export const formatMissingSkillLevelMessage = (
  requirement: EnrollmentSkillLevelRequirement,
  studentName?: string
): string =>
  `${studentName ?? "This student"} needs a ${requirement.groupLabel} skill level before they can be enrolled in this class. Select one to continue.`

/**
 * Submit-time guard. `chosenLevel` is the value from a required
 * SkillLevelDropdown rendered because the student had no level; when present
 * the requirement is treated as satisfied and the caller must persist it via
 * `buildSkillLevelStudentPatch`. Throws a user-facing Error otherwise.
 */
export const assertEnrollmentSkillLevel = (
  student: (StudentSkillSource & { name?: string }) | null | undefined,
  classObj: ClassRequirementFields | null | undefined,
  chosenLevel?: AnySkillLevel | null
): EnrollmentSkillLevelRequirement | null => {
  const missing = getMissingSkillLevelForEnrollment(student, classObj)
  if (!missing) return null
  if (chosenLevel) return missing
  throw new Error(formatMissingSkillLevelMessage(missing, student?.name))
}

/**
 * Student doc patch recording a level chosen at enrollment time, under the
 * semester the requirement covers (the class's own, so the level satisfies
 * the gate that prompted it).
 */
export const buildSkillLevelStudentPatch = (
  student: StudentSkillSource | null | undefined,
  requirement: EnrollmentSkillLevelRequirement,
  level: AnySkillLevel,
  semester: string = requirement.semester || getCurrentSemester()
): { skillHistory: StudentSkillHistory } => ({
  skillHistory: addNewSkillEntry(student?.skillHistory ?? {}, semester, {
    [requirement.skillType]: level,
  }),
})
